import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ValidatorKeyEnum } from "../../../enums/validate";
import { ITradingParameters, TRADING_PARAMS_TITLES } from "../../../interfaces/tradingParameters";
import {
  Button,
  CardHeader,
  FormError,
  FormInput,
  ParameterCard,
  ParameterRow,
} from "../style";
import { formatNumberWithCommas, parseFormattedNumber } from "../../../utils/numberFormat";

interface ITradingParameterCardProps {
  parameters: ITradingParameters;
  onUpdate: (payload: Partial<ITradingParameters>) => Promise<void>;
}

export function TradingParameterCard({ parameters, onUpdate }: ITradingParameterCardProps) {
  const [isEdit, setEdit] = useState<boolean>(false);
  const [isDisabled, setDisabled] = useState<boolean>(true);

  // Format parameters for display in the form
  const formattedParameters = {
    ...parameters,
    max_position_quote_asset: formatNumberWithCommas(parameters.max_position_quote_asset),
    max_increase_position_offset_bps: formatNumberWithCommas(parameters.max_increase_position_offset_bps),
    max_decrease_position_offset_bps: formatNumberWithCommas(parameters.max_decrease_position_offset_bps),
    amount_multiplier: formatNumberWithCommas(parameters.amount_multiplier),
    offset_multiplier: formatNumberWithCommas(parameters.offset_multiplier),
  };

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    values: formattedParameters,
  });

  const onUpdateParameters = useCallback(
    async (payload: Partial<ITradingParameters>) => {
      // Parse formatted numbers back to plain strings
      const cleanedPayload = { ...payload };
      const numberFields: Array<keyof ITradingParameters> = [
        "max_position_quote_asset",
        "max_increase_position_offset_bps",
        "max_decrease_position_offset_bps",
        "amount_multiplier",
        "offset_multiplier",
      ];

      numberFields.forEach((field) => {
        if (cleanedPayload[field]) {
          cleanedPayload[field] = parseFormattedNumber(cleanedPayload[field] as string);
        }
      });

      const difference: Partial<ITradingParameters> = {
        asset: cleanedPayload.asset,
      };

      // Check for differences
      Object.keys(parameters).forEach((key: any) => {
        const field = key as keyof ITradingParameters;
        if (Object.keys(cleanedPayload).includes(field) && parameters[field] !== cleanedPayload[field]) {
          difference[field] = cleanedPayload[field];
        }
      });

      // Ensure we have changes beyond just the asset
      if (Object.keys(difference).length > 1) {
        await onUpdate(difference);
        setEdit(false);
      }
    },
    [onUpdate, parameters]
  );

  useEffect(() => {
    if (Object.keys(errors).length > 0) return setDisabled(true);

    const formListener = watch((formData) => {
      let differenceCount = 0;

      Object.keys(parameters).forEach((key: any) => {
        const field = key as keyof ITradingParameters;
        if (Object.keys(formData).includes(field) && parameters[field] !== formData[field]) {
          differenceCount += 1;
        }
      });

      return setDisabled(differenceCount < 1);
    });

    return () => {
      formListener.unsubscribe();
    };
  }, [errors, parameters, watch]);

  useEffect(() => {
    if (isEdit === false) {
      reset();
    }
  }, [isEdit, reset]);

  const errorContent = useCallback(
    (field: keyof Omit<ITradingParameters, "asset" | "updated_at">) => (
      <>
        {errors?.[field]?.type === ValidatorKeyEnum.GtZero && (
          <FormError>{TRADING_PARAMS_TITLES[field]} must be greater than 0</FormError>
        )}
        {errors?.[field]?.type === ValidatorKeyEnum.GteZero && (
          <FormError>{TRADING_PARAMS_TITLES[field]} must be greater than or equal to 0</FormError>
        )}
      </>
    ),
    [errors]
  );

  return (
    <ParameterCard>
      <CardHeader>
        <h3>{parameters.asset}</h3>
        <div>
          {isEdit ? (
            <>
              <Button type="button" onClick={handleSubmit(onUpdateParameters)} disabled={isDisabled}>
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  reset();
                  setDisabled(false);
                  setEdit(false);
                }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button type="button" onClick={() => setEdit(true)}>
              Edit
            </Button>
          )}
        </div>
      </CardHeader>

      {isEdit ? (
        <>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_position_quote_asset}</span>
            <div>
              <FormInput
                hasErrors={!!errors.max_position_quote_asset}
                type="text"
                {...register("max_position_quote_asset", {
                  validate: {
                    [ValidatorKeyEnum.GtZero]: (v) => {
                      const numValue = Number(parseFormattedNumber(v));
                      return numValue > 0;
                    },
                  },
                })}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;
                  const rawValue = input.value;
                  const cleanValue = parseFormattedNumber(rawValue);

                  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
                    return;
                  }

                  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const formattedValue = formatNumberWithCommas(cleanValue);

                  setValue("max_position_quote_asset", formattedValue);
                  trigger("max_position_quote_asset");

                  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

                  setTimeout(() => {
                    input.setSelectionRange(newPosition, newPosition);
                  }, 0);
                }}
              />
              {errorContent("max_position_quote_asset")}
            </div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_increase_position_offset_bps}</span>
            <div>
              <FormInput
                hasErrors={!!errors.max_increase_position_offset_bps}
                type="text"
                {...register("max_increase_position_offset_bps", {
                  validate: {
                    [ValidatorKeyEnum.GteZero]: (v) => {
                      const numValue = Number(parseFormattedNumber(v));
                      return numValue >= 0;
                    },
                  },
                })}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;
                  const rawValue = input.value;
                  const cleanValue = parseFormattedNumber(rawValue);

                  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
                    return;
                  }

                  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const formattedValue = formatNumberWithCommas(cleanValue);

                  setValue("max_increase_position_offset_bps", formattedValue);
                  trigger("max_increase_position_offset_bps");

                  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

                  setTimeout(() => {
                    input.setSelectionRange(newPosition, newPosition);
                  }, 0);
                }}
              />
              {errorContent("max_increase_position_offset_bps")}
            </div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_decrease_position_offset_bps}</span>
            <div>
              <FormInput
                hasErrors={!!errors.max_decrease_position_offset_bps}
                type="text"
                {...register("max_decrease_position_offset_bps", {
                  validate: {
                    [ValidatorKeyEnum.GteZero]: (v) => {
                      const numValue = Number(parseFormattedNumber(v));
                      return numValue >= 0;
                    },
                  },
                })}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;
                  const rawValue = input.value;
                  const cleanValue = parseFormattedNumber(rawValue);

                  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
                    return;
                  }

                  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const formattedValue = formatNumberWithCommas(cleanValue);

                  setValue("max_decrease_position_offset_bps", formattedValue);
                  trigger("max_decrease_position_offset_bps");

                  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

                  setTimeout(() => {
                    input.setSelectionRange(newPosition, newPosition);
                  }, 0);
                }}
              />
              {errorContent("max_decrease_position_offset_bps")}
            </div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.amount_multiplier}</span>
            <div>
              <FormInput
                hasErrors={!!errors.amount_multiplier}
                type="text"
                {...register("amount_multiplier", {
                  validate: {
                    [ValidatorKeyEnum.GtZero]: (v) => {
                      const numValue = Number(parseFormattedNumber(v));
                      return numValue > 0;
                    },
                  },
                })}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;
                  const rawValue = input.value;
                  const cleanValue = parseFormattedNumber(rawValue);

                  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
                    return;
                  }

                  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const formattedValue = formatNumberWithCommas(cleanValue);

                  setValue("amount_multiplier", formattedValue);
                  trigger("amount_multiplier");

                  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

                  setTimeout(() => {
                    input.setSelectionRange(newPosition, newPosition);
                  }, 0);
                }}
              />
              {errorContent("amount_multiplier")}
            </div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.offset_multiplier}</span>
            <div>
              <FormInput
                hasErrors={!!errors.offset_multiplier}
                type="text"
                {...register("offset_multiplier", {
                  validate: {
                    [ValidatorKeyEnum.GtZero]: (v) => {
                      const numValue = Number(parseFormattedNumber(v));
                      return numValue > 0;
                    },
                  },
                })}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;
                  const rawValue = input.value;
                  const cleanValue = parseFormattedNumber(rawValue);

                  if (cleanValue !== "" && Number.isNaN(Number(cleanValue))) {
                    return;
                  }

                  const commasBeforeCursor = (rawValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const formattedValue = formatNumberWithCommas(cleanValue);

                  setValue("offset_multiplier", formattedValue);
                  trigger("offset_multiplier");

                  const commasInFormatted = (formattedValue.slice(0, cursorPosition).match(/,/g) || []).length;
                  const newPosition = cursorPosition + (commasInFormatted - commasBeforeCursor);

                  setTimeout(() => {
                    input.setSelectionRange(newPosition, newPosition);
                  }, 0);
                }}
              />
              {errorContent("offset_multiplier")}
            </div>
          </ParameterRow>
        </>
      ) : (
        <>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_position_quote_asset}</span>
            <div>{formatNumberWithCommas(parameters.max_position_quote_asset)}</div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_increase_position_offset_bps}</span>
            <div>{formatNumberWithCommas(parameters.max_increase_position_offset_bps)}</div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.max_decrease_position_offset_bps}</span>
            <div>{formatNumberWithCommas(parameters.max_decrease_position_offset_bps)}</div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.amount_multiplier}</span>
            <div>{formatNumberWithCommas(parameters.amount_multiplier)}</div>
          </ParameterRow>
          <ParameterRow>
            <span>{TRADING_PARAMS_TITLES.offset_multiplier}</span>
            <div>{formatNumberWithCommas(parameters.offset_multiplier)}</div>
          </ParameterRow>
        </>
      )}
    </ParameterCard>
  );
}
